import { Send } from "lucide-react";
import type React from "react";
import { useCallback, useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { EmojiPickerButton } from "@/components/common/EmojiPickerButton";
import { FileAttachment } from "@/components/common/FileAttachment";
import { FormattingToolbar } from "@/components/common/FormattingToolbar";
import { MentionSuggestions } from "@/components/common/MentionSuggestions";
import { TextEditor } from "@/components/common/TextEditor";
import { Button } from "@/components/ui/button";
import { ConfirmationDialog } from "@/components/ui/confirmation-dialog";
import { POST_MAX_FILE_SIZE } from "@/constants";
import { buildComposerDraftKey } from "@/features/composer-drafts/draftKey";
import type { DraftStorageError } from "@/features/composer-drafts/draftRepository";
import type { ComposerDraftScope } from "@/features/composer-drafts/types";
import { useComposerDraft } from "@/features/composer-drafts/useComposerDraft";
import { useEditorLinkHover } from "@/hooks/useEditorLinkHover";
import { useEditorSelection } from "@/hooks/useEditorSelection";
import { useFileAttachment } from "@/hooks/useFileAttachment";
import { useFormatting } from "@/hooks/useFormatting";
import { useMention } from "@/hooks/useMention";
import { usePasteHandler } from "@/hooks/usePasteHandler";
import type { MentionCandidate, User } from "@/lib/humhub-api/types";
import {
  clearUnsavedPostInput,
  setUnsavedPostInput,
} from "@/lib/unsavedPostInputGuard";
import {
  convertMarkdownToEditorHtml,
  convertTextToMarkdown,
} from "@/lib/utils/markdownUtils";

// インライン画像貼り付け時の制限値
const MAX_INLINE_IMAGE_COUNT = 5;
const MAX_INLINE_IMAGE_TOTAL_BYTES = 8 * 1024 * 1024;

interface EditorAlertsProps {
  imageError: string | null;
  lengthError: string | null;
}

const Alert: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="mt-2 rounded border border-red-200 bg-red-50 p-2 text-sm text-red-500">
    {children}
  </div>
);

const EditorAlerts: React.FC<EditorAlertsProps> = ({
  imageError,
  lengthError,
}) => {
  if (!imageError && !lengthError) {
    return null;
  }

  return (
    <>
      {imageError && <Alert>{imageError}</Alert>}
      {lengthError && <Alert>{lengthError}</Alert>}
    </>
  );
};

interface EditorFooterProps {
  isVisible: boolean;
  showFormatting: boolean;
  showAttachButton?: boolean;
  formattingState: {
    isBold: boolean;
    isItalic: boolean;
    isLink: boolean;
    isCode: boolean;
    isBlockquote: boolean;
    isHeading1: boolean;
    isHeading2: boolean;
    isUnorderedList: boolean;
    isOrderedList: boolean;
  };
  onToggleBold: () => void;
  onToggleItalic: () => void;
  onApplyLink: (url: string) => void;
  onRemoveLink: () => void;
  onBeforeLinkEdit: () => void;
  onToggleCode: () => void;
  onToggleBlockquote: () => void;
  onToggleHeading1: () => void;
  onToggleHeading2: () => void;
  onToggleUnorderedList: () => void;
  onToggleOrderedList: () => void;
  onIndent: () => void;
  onOutdent: () => void;
  onAttachClick?: () => void;
  fileInputRef?: React.RefObject<HTMLInputElement | null>;
  onFileChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onSubmit: () => void;
  submitButtonLabel?: string;
  cancelButtonLabel?: string;
  onCancel?: () => void;
  canSubmit: boolean;
  disabled?: boolean;
  submitDisabled?: boolean;
  draftTargetUnavailable?: boolean;
  emojiPicker?: React.ReactNode;
  onLinkPopoverOpenChange?: (open: boolean) => void;
  submitIconOnly?: boolean;
}

const EditorFooter: React.FC<EditorFooterProps> = ({
  isVisible,
  showFormatting,
  showAttachButton = true,
  formattingState,
  onToggleBold,
  onToggleItalic,
  onApplyLink,
  onRemoveLink,
  onBeforeLinkEdit,
  onToggleCode,
  onToggleBlockquote,
  onToggleHeading1,
  onToggleHeading2,
  onToggleUnorderedList,
  onToggleOrderedList,
  onIndent,
  onOutdent,
  onAttachClick,
  fileInputRef,
  onFileChange,
  onSubmit,
  submitButtonLabel,
  cancelButtonLabel,
  onCancel,
  canSubmit,
  disabled,
  submitDisabled = false,
  draftTargetUnavailable = false,
  emojiPicker,
  onLinkPopoverOpenChange,
  submitIconOnly = false,
}) => {
  if (!isVisible) {
    return null;
  }

  return (
    <div
      data-editor-footer
      className="flex w-full min-w-0 items-center gap-2 overflow-hidden border-t border-custom-border bg-white p-2 [@media(max-height:700px)]:gap-1.5 [@media(max-height:700px)]:py-1.5"
    >
      <div className="min-w-0 max-w-full flex-1 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {showFormatting && (
          <FormattingToolbar
            isBold={formattingState.isBold}
            isItalic={formattingState.isItalic}
            isLink={formattingState.isLink}
            isCode={formattingState.isCode}
            isBlockquote={formattingState.isBlockquote}
            isHeading1={formattingState.isHeading1}
            isHeading2={formattingState.isHeading2}
            isUnorderedList={formattingState.isUnorderedList}
            isOrderedList={formattingState.isOrderedList}
            onToggleBold={onToggleBold}
            onToggleItalic={onToggleItalic}
            onApplyLink={onApplyLink}
            onRemoveLink={onRemoveLink}
            onBeforeLinkEdit={onBeforeLinkEdit}
            onToggleCode={onToggleCode}
            onToggleBlockquote={onToggleBlockquote}
            onToggleHeading1={onToggleHeading1}
            onToggleHeading2={onToggleHeading2}
            onToggleUnorderedList={onToggleUnorderedList}
            onToggleOrderedList={onToggleOrderedList}
            onIndent={onIndent}
            onOutdent={onOutdent}
            onAttachClick={onAttachClick}
            fileInputRef={fileInputRef}
            onFileChange={onFileChange}
            showAttachButton={showAttachButton}
            emojiPicker={emojiPicker}
            onLinkPopoverOpenChange={onLinkPopoverOpenChange}
          />
        )}
      </div>
      <div className="flex shrink-0 items-center gap-2 [@media(max-height:700px)]:gap-1">
        {onCancel && (
          <Button
            variant="ghost"
            size="sm"
            onMouseDown={(e) => {
              e.preventDefault();
            }}
            onClick={onCancel}
            className="h-8 px-2 text-xs text-gray-500 hover:text-gray-700"
            type="button"
          >
            {cancelButtonLabel}
          </Button>
        )}
        <Button
          variant="submit"
          size="sm"
          onClick={onSubmit}
          disabled={
            !canSubmit || disabled || submitDisabled || draftTargetUnavailable
          }
          type="button"
          aria-label={submitIconOnly ? submitButtonLabel : undefined}
          className={
            submitIconOnly
              ? "size-10 rounded-full bg-[#0a505e] p-0 hover:bg-[#073d48]"
              : undefined
          }
        >
          {submitIconOnly ? (
            <Send className="size-5" aria-hidden="true" />
          ) : (
            submitButtonLabel
          )}
        </Button>
      </div>
    </div>
  );
};

export interface BaseInputEditorVisibilityProps {
  alwaysVisible?: boolean;
}

interface BaseInputEditorProps extends BaseInputEditorVisibilityProps {
  placeholder?: string;
  submitButtonLabel?: string;
  cancelButtonLabel?: string;
  onCancel?: () => void;
  showFormatting?: boolean;
  showAttachButton?: boolean;
  onContentChange?: (content: string) => void;
  submitDisabled?: boolean;
  hasDiscardableChanges?: boolean;
  externalOnAttachClick?: () => void;
  externalFileInputRef?: React.RefObject<HTMLInputElement | null>;
  externalOnFileChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onSubmit?: (content: string, files?: File[]) => Promise<void>;
  className?: string;
  submitIconOnly?: boolean;
  disabled?: boolean;
  maxFileSize?: number;
  maxFileCount?: number;
  allowedFileTypes?: string[];
  onMentionTrigger?: (query: string) => Promise<User[]>;
  mentionScopeKey?: string;
  mentionUsers?: User[];
  isLoadingMentionUsers?: boolean;
  hasMoreMentionUsers?: boolean;
  isFetchingMoreMentionUsers?: boolean;
  mentionLoadMoreError?: string | null;
  onLoadMoreMentionUsers?: () => Promise<User[]>;
  context: "post" | "chat";
  autoFocus?: boolean;
  maxLength?: number;
  onFocusChange?: (isFocused: boolean) => void;
  initialContent?: string;
  initialFiles?: File[];
  initialDraftKey?: string;
  draftScope?: ComposerDraftScope;
  onDraftRestored?: () => void;
  onDraftRestorationSettled?: () => void;
  onDraftStorageError?: (error: DraftStorageError) => void;
  /** The original comment/reply target no longer accepts submissions. */
  draftTargetUnavailable?: boolean;
}

const disabledDraftScope: ComposerDraftScope = {
  kind: "post",
  userId: -1,
  studyGroupId: -1,
  channelKey: "disabled",
};

export const BaseInputEditor: React.FC<BaseInputEditorProps> = ({
  placeholder,
  submitButtonLabel,
  cancelButtonLabel,
  onCancel,
  showFormatting = true,
  showAttachButton = true,
  onContentChange,
  submitDisabled = false,
  hasDiscardableChanges,
  externalOnAttachClick,
  externalFileInputRef,
  externalOnFileChange,
  onSubmit,
  className = "",
  submitIconOnly = false,
  disabled,
  maxFileSize,
  maxFileCount,
  allowedFileTypes,
  onMentionTrigger,
  mentionScopeKey,
  mentionUsers = [],
  isLoadingMentionUsers = false,
  hasMoreMentionUsers = false,
  isFetchingMoreMentionUsers = false,
  mentionLoadMoreError,
  onLoadMoreMentionUsers,
  context,
  autoFocus = false,
  maxLength,
  alwaysVisible = false,
  onFocusChange,
  initialContent,
  initialFiles,
  initialDraftKey,
  draftScope,
  onDraftRestored,
  onDraftRestorationSettled,
  onDraftStorageError,
  draftTargetUnavailable = false,
}) => {
  const { t } = useTranslation();
  const [isFocused, setIsFocused] = useState(false);
  const [content, setContent] = useState("");
  const [imageError, setImageError] = useState<string | null>(null);
  const [lengthError, setLengthError] = useState<string | null>(null);
  const [isEmojiPickerOpen, setIsEmojiPickerOpen] = useState(false);
  const [isLinkPopoverOpen, setIsLinkPopoverOpen] = useState(false);
  const [isDiscardDialogOpen, setIsDiscardDialogOpen] = useState(false);
  const [submittedDraftCleanupPending, setSubmittedDraftCleanupPending] =
    useState(false);
  const unsavedPostSourceRef = useRef(Symbol("post-composer"));

  const containerRef = useRef<HTMLDivElement>(null);
  const editorRef = useRef<HTMLDivElement>(null);
  const mentionListboxId = useId();
  const hasAutoFocusedRef = useRef(false);
  const appliedInitialContentKeyRef = useRef<string | null>(null);
  const pendingDraftRestoreRef = useRef<string | null>(null);
  const [restoredAttachmentScopeKey, setRestoredAttachmentScopeKey] = useState<
    string | null
  >(null);
  const draftScopeKey = draftScope ? buildComposerDraftKey(draftScope) : null;
  const hasRestoredAttachments = restoredAttachmentScopeKey === draftScopeKey;
  const { savedSelectionRef, restoreSelection, saveCurrentSelection } =
    useEditorSelection({ editorRef });
  const { hoveredLink, handlePointerOver, handlePointerOut } =
    useEditorLinkHover({ editorRef, containerRef });

  // autoFocus機能
  useEffect(() => {
    if (
      autoFocus &&
      editorRef.current &&
      !disabled &&
      !hasAutoFocusedRef.current
    ) {
      hasAutoFocusedRef.current = true;
      editorRef.current.focus();
      setIsFocused(true);
      onFocusChange?.(true);
    }
  }, [autoFocus, disabled, onFocusChange]);

  useEffect(() => {
    return () => {
      hasAutoFocusedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!autoFocus) {
      hasAutoFocusedRef.current = false;
    }
  }, [autoFocus]);

  const mentionHook = useMention({
    onFetchUsers: async (query) => {
      if (onMentionTrigger) {
        return onMentionTrigger(query);
      }
      return mentionUsers;
    },
    context,
    includeStudyGroup: true,
    scopeKey: mentionScopeKey,
    hasMoreCandidates: hasMoreMentionUsers,
    isFetchingMoreCandidates: isFetchingMoreMentionUsers,
    onFetchMoreCandidates: onLoadMoreMentionUsers,
    mentionUsers,
  });

  const formattingHook = useFormatting({ editorRef });

  const fileAttachmentHook = useFileAttachment({
    maxFileSize,
    maxFileCount,
    allowedFileTypes,
    initialFiles,
    initialFilesKey: initialDraftKey,
    onFilesChange: (files) => {
      setRestoredAttachmentScopeKey(null);
      if (files.length > 0) {
        setIsFocused(true);
        onFocusChange?.(true);
      }
    },
  });

  const applyDraftRestore = useCallback(
    (restoredContent: string) => {
      pendingDraftRestoreRef.current = restoredContent;
      if (!editorRef.current) return;

      editorRef.current.innerHTML =
        convertMarkdownToEditorHtml(restoredContent);
      pendingDraftRestoreRef.current = null;
      setContent(restoredContent);
      setIsFocused(true);
      onFocusChange?.(true);
      onDraftRestored?.();
    },
    [onDraftRestored, onFocusChange],
  );

  const {
    restoredDraft,
    discardDraft,
    flushDraft,
    isRestoring: isDraftRestoring,
    isTargetUnavailable: restoredTargetUnavailable,
    storageError: draftStorageError,
  } = useComposerDraft({
    scope: draftScope ?? disabledDraftScope,
    content,
    hadAttachments:
      fileAttachmentHook.attachedFiles.length > 0 || hasRestoredAttachments,
    enabled: draftScope !== undefined,
    onRestore: applyDraftRestore,
  });
  const observedDraftRestorationRef = useRef(false);
  useEffect(() => {
    if (isDraftRestoring) {
      observedDraftRestorationRef.current = true;
      return;
    }
    if (!observedDraftRestorationRef.current) return;
    observedDraftRestorationRef.current = false;
    onDraftRestorationSettled?.();
  }, [isDraftRestoring, onDraftRestorationSettled]);
  const isDraftTargetUnavailable =
    draftTargetUnavailable || restoredTargetUnavailable;

  useEffect(() => {
    if (restoredDraft?.hadAttachments && draftScopeKey) {
      setRestoredAttachmentScopeKey(draftScopeKey);
    }
  }, [draftScopeKey, restoredDraft]);

  useEffect(() => {
    const pendingRestore = pendingDraftRestoreRef.current;
    if (!pendingRestore || !editorRef.current) return;
    applyDraftRestore(pendingRestore);
  }, [applyDraftRestore]);

  useEffect(() => {
    if (draftStorageError) onDraftStorageError?.(draftStorageError);
  }, [draftStorageError, onDraftStorageError]);

  useEffect(() => {
    if (!initialContent || !editorRef.current) {
      return;
    }

    const initialContentKey = initialDraftKey ?? initialContent;
    if (appliedInitialContentKeyRef.current === initialContentKey) {
      return;
    }
    if (editorRef.current.textContent?.trim()) {
      return;
    }

    editorRef.current.innerHTML = convertMarkdownToEditorHtml(initialContent);
    setContent(initialContent);
    setIsFocused(true);
    onFocusChange?.(true);
    appliedInitialContentKeyRef.current = initialContentKey;
  }, [initialContent, initialDraftKey, onFocusChange]);

  const validateContent = useCallback((): {
    isValid: boolean;
    contentToSubmit: string;
    lengthError?: string;
  } => {
    if (!editorRef.current) return { isValid: false, contentToSubmit: "" };

    const contentToSubmit = convertTextToMarkdown(
      editorRef.current,
      showFormatting,
    );
    const hasContent = contentToSubmit.trim().length > 0;
    if (!hasContent) {
      return { isValid: false, contentToSubmit };
    }

    if (maxLength && contentToSubmit.length > maxLength) {
      return {
        isValid: false,
        contentToSubmit,
        lengthError: t("common.maxLengthExceeded", { maxLength }),
      };
    }

    return { isValid: true, contentToSubmit };
  }, [maxLength, showFormatting, t]);

  const submitContent = useCallback(
    async (contentToSubmit: string) => {
      await onSubmit?.(
        contentToSubmit,
        fileAttachmentHook.attachedFiles.length > 0
          ? fileAttachmentHook.attachedFiles
          : undefined,
      );
    },
    [fileAttachmentHook, onSubmit],
  );

  const resetEditorState = useCallback(() => {
    if (!editorRef.current) return;

    editorRef.current.innerHTML = "";
    editorRef.current.textContent = "";
    setContent("");
    fileAttachmentHook.clearFiles();
    setIsFocused(false);
    onFocusChange?.(false);
    editorRef.current.blur();
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    setImageError(null);
    setLengthError(null);
    mentionHook.hideMentionSuggestions();
    formattingHook.resetFormatState();
    setSubmittedDraftCleanupPending(false);
  }, [fileAttachmentHook, formattingHook, mentionHook, onFocusChange]);

  const { handlePaste } = usePasteHandler({
    editorRef,
    maxInlineImageCount: MAX_INLINE_IMAGE_COUNT,
    maxInlineImageTotalBytes: MAX_INLINE_IMAGE_TOTAL_BYTES,
    maxFileSize: maxFileSize ?? POST_MAX_FILE_SIZE,
    translate: t,
    onImageError: (message) => setImageError(message),
    onInlineImageAdd: (file) => fileAttachmentHook.addFiles([file]),
    getCurrentAttachmentCount: () => fileAttachmentHook.attachedFiles.length,
    getCurrentAttachmentTotalBytes: () =>
      fileAttachmentHook.totalAttachmentSize,
  });

  const handleInput = (e: React.FormEvent<HTMLDivElement>) => {
    const target = e.target as HTMLDivElement;
    const newContent = convertTextToMarkdown(target, showFormatting);
    onContentChange?.(newContent);

    if (newContent !== content) {
      setContent(newContent);
    }

    // 画像エラーをクリア
    if (imageError) {
      setImageError(null);
    }

    if (maxLength && editorRef.current) {
      const validation = validateContent();
      setLengthError(validation.lengthError ?? null);
    } else {
      setLengthError(null);
    }

    formattingHook.updateFormatState();

    const selection = window.getSelection();
    mentionHook.handleTextInput(selection, editorRef);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Enter" && e.ctrlKey) {
      handleSubmit();
      return;
    }

    const mentionHandled = mentionHook.handleKeyDown(e);
    if (mentionHandled) {
      if (
        e.key === "Enter" &&
        mentionHook.selectedMentionIndex <
          mentionHook.filteredMentionCandidates.length
      ) {
        const selectedCandidate =
          mentionHook.filteredMentionCandidates[
            mentionHook.selectedMentionIndex
          ];
        handleMentionSelect(selectedCandidate);
      }
      return;
    }
  };

  const handleSubmit = async () => {
    if (isDraftTargetUnavailable || submittedDraftCleanupPending) return;
    const {
      isValid,
      contentToSubmit,
      lengthError: validationError,
    } = validateContent();
    if (!isValid) return;

    setLengthError(validationError ?? null);

    let submissionSucceeded = false;
    try {
      if (draftScope) await flushDraft();
      await submitContent(contentToSubmit);
      submissionSucceeded = true;
      if (draftScope) await discardDraft();
      setRestoredAttachmentScopeKey(null);
      resetEditorState();
    } catch (error) {
      if (submissionSucceeded) setSubmittedDraftCleanupPending(true);
      console.error(`Error submitting ${context}:`, error);
    }
  };

  const handleMentionSelect = (item: MentionCandidate) => {
    mentionHook.insertMention(item, editorRef);

    setTimeout(() => {
      if (editorRef.current) {
        const newContent = convertTextToMarkdown(
          editorRef.current,
          showFormatting,
        );
        setContent(newContent);

        // Check content length using the same logic as other input handlers
        checkContentLength();
      }
    }, 0);
  };

  const checkContentLength = useCallback(() => {
    if (!editorRef.current) return;

    if (!maxLength) {
      setLengthError(null);
      return;
    }

    setTimeout(() => {
      if (editorRef.current) {
        const contentToCheck = convertTextToMarkdown(
          editorRef.current,
          showFormatting,
        );

        if (contentToCheck.length > maxLength) {
          setLengthError(t("common.maxLengthExceeded", { maxLength }));
        } else {
          setLengthError(null);
        }
      }
    }, 0);
  }, [maxLength, showFormatting, t]);

  const handleToggleBold = useCallback(() => {
    formattingHook.toggleBold();
    checkContentLength();
  }, [formattingHook, checkContentLength]);

  const handleToggleItalic = useCallback(() => {
    formattingHook.toggleItalic();
    checkContentLength();
  }, [formattingHook, checkContentLength]);

  const handleApplyLink = useCallback(
    (url: string) => {
      restoreSelection();
      const selection = window.getSelection();
      const range = selection?.rangeCount ? selection.getRangeAt(0) : null;

      if (selection && range?.collapsed && editorRef.current) {
        const link = document.createElement("a");
        link.href = url;
        link.textContent = url;
        range.insertNode(link);

        const caret = document.createRange();
        caret.setStartAfter(link);
        caret.collapse(true);
        selection.removeAllRanges();
        selection.addRange(caret);
        savedSelectionRef.current = caret.cloneRange();
        setContent(convertTextToMarkdown(editorRef.current, showFormatting));
        formattingHook.updateFormatState();
      } else {
        formattingHook.applyLink(url);
      }
      checkContentLength();
    },
    [
      restoreSelection,
      formattingHook,
      checkContentLength,
      savedSelectionRef,
      showFormatting,
    ],
  );

  const handleRemoveLink = useCallback(() => {
    restoreSelection();
    const selection = window.getSelection();
    const range = selection?.rangeCount ? selection.getRangeAt(0) : null;
    const startElement =
      range?.startContainer.nodeType === Node.ELEMENT_NODE
        ? (range.startContainer as Element)
        : range?.startContainer.parentElement;
    const link = startElement?.closest("a");

    if (selection && range?.collapsed && link) {
      const caret = document.createRange();
      caret.setStartAfter(link);
      caret.collapse(true);
      selection.removeAllRanges();
      selection.addRange(caret);
      savedSelectionRef.current = caret.cloneRange();
      formattingHook.updateFormatState();
      checkContentLength();
      return;
    }

    formattingHook.removeLink();
    checkContentLength();
  }, [restoreSelection, formattingHook, checkContentLength, savedSelectionRef]);

  const handleToggleCode = useCallback(() => {
    formattingHook.toggleCode();
    checkContentLength();
  }, [formattingHook, checkContentLength]);

  const handleToggleBlockquote = useCallback(() => {
    formattingHook.toggleBlockquote();
    checkContentLength();
  }, [formattingHook, checkContentLength]);

  const handleToggleHeading1 = useCallback(() => {
    formattingHook.toggleHeading1();
    checkContentLength();
  }, [formattingHook, checkContentLength]);

  const handleToggleHeading2 = useCallback(() => {
    formattingHook.toggleHeading2();
    checkContentLength();
  }, [formattingHook, checkContentLength]);

  const insertEmojiAtCaret = (emoji: string) => {
    if (!editorRef.current) return;

    editorRef.current.focus();
    const selection = window.getSelection();
    if (!selection) return;

    selection.removeAllRanges();
    if (savedSelectionRef.current) {
      selection.addRange(savedSelectionRef.current);
    } else {
      const range = document.createRange();
      range.selectNodeContents(editorRef.current);
      range.collapse(false);
      selection.addRange(range);
    }

    const activeRange = selection.getRangeAt(0);
    activeRange.deleteContents();

    const textNode = document.createTextNode(emoji);
    activeRange.insertNode(textNode);

    const afterRange = document.createRange();
    afterRange.setStartAfter(textNode);
    afterRange.collapse(true);
    selection.removeAllRanges();
    selection.addRange(afterRange);
    savedSelectionRef.current = afterRange.cloneRange();

    const updatedContent = convertTextToMarkdown(
      editorRef.current,
      showFormatting,
    );
    setContent(updatedContent);
    setIsFocused(true);
    checkContentLength();
  };

  const isExpanded =
    alwaysVisible ||
    isFocused ||
    fileAttachmentHook.attachedFiles.length > 0 ||
    isEmojiPickerOpen ||
    isLinkPopoverOpen;
  const isEmpty = content.trim() === "";
  const canSubmit = !isEmpty && !lengthError;
  const hasUnsavedPostInput =
    context === "post" &&
    (hasDiscardableChanges ??
      (content.trim().length > 0 ||
        fileAttachmentHook.attachedFiles.length > 0));

  useEffect(() => {
    const source = unsavedPostSourceRef.current;
    setUnsavedPostInput(source, hasUnsavedPostInput);

    return () => clearUnsavedPostInput(source);
  }, [hasUnsavedPostInput]);

  useEffect(() => {
    if (!hasUnsavedPostInput) return;

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };
    const handleDocumentClick = (event: MouseEvent) => {
      if (event.defaultPrevented) return;
      const target = event.target;
      if (!(target instanceof Element)) return;
      const link = target.closest<HTMLAnchorElement>("a[href]");
      if (!link || link.target === "_blank" || link.hasAttribute("download")) {
        return;
      }
      if (containerRef.current?.contains(link)) return;
      if (window.confirm(t("common.discardPostNavigation"))) return;

      event.preventDefault();
      event.stopImmediatePropagation();
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    document.addEventListener("click", handleDocumentClick, true);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      document.removeEventListener("click", handleDocumentClick, true);
    };
  }, [hasUnsavedPostInput, t]);

  const handleCancelRequest = () => {
    if (
      hasDiscardableChanges ??
      (content.trim().length > 0 || fileAttachmentHook.attachedFiles.length > 0)
    ) {
      setIsDiscardDialogOpen(true);
      return;
    }

    onCancel?.();
  };
  const handleDiscard = async () => {
    try {
      if (draftScope) await discardDraft();
      setIsDiscardDialogOpen(false);
      setRestoredAttachmentScopeKey(null);
      resetEditorState();
      onCancel?.();
    } catch (error) {
      console.error(`Error discarding ${context} draft:`, error);
    }
  };
  const handleRetrySubmittedDraftCleanup = async () => {
    if (!draftScope) return;
    try {
      await discardDraft();
      setRestoredAttachmentScopeKey(null);
      resetEditorState();
    } catch (error) {
      console.error(`Error retrying ${context} draft cleanup:`, error);
    }
  };
  const handleCopyDraft = useCallback(() => {
    if (!content) return;
    void navigator.clipboard?.writeText(content).catch(() => undefined);
  }, [content]);
  const isMentionSuggestionsLoading =
    isLoadingMentionUsers || mentionHook.isLoadingMentionUsers;
  const shouldShowStickyFooter =
    isExpanded ||
    fileAttachmentHook.attachedFiles.length > 0 ||
    Boolean(fileAttachmentHook.fileError);

  return (
    <div
      ref={containerRef}
      className={`base-input-editor rounded-2xl border-2 border-custom-border bg-white p-3 shadow-sm [@media(max-height:700px)]:p-1.5 ${className} relative`}
      onPointerOver={handlePointerOver}
      onPointerOut={handlePointerOut}
    >
      <ConfirmationDialog
        open={isDiscardDialogOpen}
        onOpenChange={setIsDiscardDialogOpen}
        title={t("common.discardPostTitle")}
        description={t("common.discardPostDescription")}
        confirmText={t("common.discardPost")}
        cancelText={t("common.continueEditing")}
        onConfirm={handleDiscard}
        variant="destructive"
        closeOnConfirm={false}
      />
      <TextEditor
        ref={editorRef}
        isMentionEnabled
        ariaControls={mentionListboxId}
        ariaExpanded={mentionHook.showMentionSuggestions}
        ariaActiveDescendant={
          mentionHook.showMentionSuggestions &&
          !isMentionSuggestionsLoading &&
          mentionHook.selectedMentionIndex >= 0 &&
          mentionHook.selectedMentionIndex <
            mentionHook.filteredMentionCandidates.length +
              (hasMoreMentionUsers ? 1 : 0)
            ? `${mentionListboxId}-option-${mentionHook.selectedMentionIndex}`
            : undefined
        }
        ariaAutocomplete="list"
        placeholder={placeholder}
        disabled={disabled || submittedDraftCleanupPending}
        onFocus={() => {
          setIsFocused(true);
          onFocusChange?.(true);
        }}
        onInput={handleInput}
        onKeyDown={handleKeyDown}
        onPaste={handlePaste}
        className={
          isExpanded
            ? "min-h-6 pt-1"
            : "h-6 min-h-6 !leading-6 [&:empty]:before:!leading-6"
        }
      />

      {hoveredLink && (
        <div
          className="absolute z-50 rounded bg-slate-800 px-2 py-1 text-xs text-white shadow"
          style={{
            top: hoveredLink.top,
            left: hoveredLink.left,
            maxWidth: "300px",
            wordWrap: "break-word",
          }}
        >
          {hoveredLink.url}
        </div>
      )}

      <EditorAlerts imageError={imageError} lengthError={lengthError} />

      {draftStorageError && (
        <output className="mt-2 block text-sm text-amber-700">
          {t("common.draftStorageError")}
        </output>
      )}

      {submittedDraftCleanupPending && (
        <div className="mt-2 rounded border border-amber-200 bg-amber-50 p-2 text-sm text-amber-900">
          <output className="block">
            {t("common.draftSubmittedCleanupError")}
          </output>
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="mt-2"
            onClick={() => void handleRetrySubmittedDraftCleanup()}
          >
            {t("common.retry")}
          </Button>
        </div>
      )}

      {isDraftTargetUnavailable && (
        <div className="mt-2 rounded border border-amber-200 bg-amber-50 p-2 text-sm text-amber-900">
          <output className="block">
            {t("common.draftTargetUnavailable")}
          </output>
          <div className="mt-2 flex gap-2">
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={handleCopyDraft}
            >
              {t("common.copyDraft")}
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => void handleDiscard()}
            >
              {t("common.discardDraft")}
            </Button>
          </div>
        </div>
      )}

      <MentionSuggestions
        id={mentionListboxId}
        isVisible={mentionHook.showMentionSuggestions}
        position={mentionHook.mentionPosition}
        candidates={mentionHook.filteredMentionCandidates || []}
        selectedIndex={mentionHook.selectedMentionIndex}
        isLoading={isMentionSuggestionsLoading}
        hasMore={hasMoreMentionUsers}
        isFetchingMore={isFetchingMoreMentionUsers}
        loadMoreError={mentionLoadMoreError}
        onFetchMore={() => void mentionHook.fetchMoreMentionUsers()}
        onSelect={handleMentionSelect}
        onClose={mentionHook.hideMentionSuggestions}
        suggestionsRef={mentionHook.mentionSuggestionsRef}
      />

      {shouldShowStickyFooter && (
        <div
          className={`sticky bottom-0 z-20 -mx-3 bg-white px-3 shadow-[0_-4px_8px_rgba(255,255,255,0.85)] [@media(max-height:700px)]:mx-0 [@media(max-height:700px)]:px-0 [@media(max-height:700px)]:shadow-none ${isExpanded ? "mt-2" : "mt-3"}`}
        >
          <FileAttachment
            files={fileAttachmentHook.attachedFiles}
            fileError={fileAttachmentHook.fileError}
            onRemoveFile={fileAttachmentHook.removeFile}
          />

          <EditorFooter
            isVisible={isExpanded}
            showFormatting={showFormatting}
            showAttachButton={showAttachButton}
            formattingState={{
              isBold: formattingHook.isBold,
              isItalic: formattingHook.isItalic,
              isLink: formattingHook.isLink,
              isCode: formattingHook.isCode,
              isBlockquote: formattingHook.isBlockquote,
              isHeading1: formattingHook.isHeading1,
              isHeading2: formattingHook.isHeading2,
              isUnorderedList: formattingHook.isUnorderedList,
              isOrderedList: formattingHook.isOrderedList,
            }}
            onToggleBold={handleToggleBold}
            onToggleItalic={handleToggleItalic}
            onApplyLink={handleApplyLink}
            onRemoveLink={handleRemoveLink}
            onBeforeLinkEdit={saveCurrentSelection}
            onToggleCode={handleToggleCode}
            onToggleBlockquote={handleToggleBlockquote}
            onToggleHeading1={handleToggleHeading1}
            onToggleHeading2={handleToggleHeading2}
            onToggleUnorderedList={formattingHook.toggleUnorderedList}
            onToggleOrderedList={formattingHook.toggleOrderedList}
            onIndent={formattingHook.indent}
            onOutdent={formattingHook.outdent}
            onAttachClick={
              externalOnAttachClick ?? fileAttachmentHook.handleAttachClick
            }
            fileInputRef={
              externalFileInputRef ?? fileAttachmentHook.fileInputRef
            }
            onFileChange={
              externalOnFileChange ?? fileAttachmentHook.handleFileChange
            }
            onSubmit={handleSubmit}
            submitButtonLabel={submitButtonLabel}
            cancelButtonLabel={cancelButtonLabel || t("common.cancel")}
            onCancel={handleCancelRequest}
            canSubmit={canSubmit}
            disabled={disabled || submittedDraftCleanupPending}
            submitDisabled={submitDisabled || submittedDraftCleanupPending}
            draftTargetUnavailable={isDraftTargetUnavailable}
            emojiPicker={
              <EmojiPickerButton
                onBeforeOpen={saveCurrentSelection}
                onSelect={insertEmojiAtCaret}
                onOpenChange={setIsEmojiPickerOpen}
              />
            }
            onLinkPopoverOpenChange={setIsLinkPopoverOpen}
            submitIconOnly={submitIconOnly}
          />
        </div>
      )}
    </div>
  );
};
